import { useState, useMemo, useCallback, useRef } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IMppNode, loadMppTree, saveMppTree } from "data/mpp";
// Ten sam styl, co struktury JRWA / WPK gminy.
import styles from "../JrwaTab/styles.module.scss";

// ── Operacje na drzewie ───────────────────────────────────────────────────────

const collectAllIds = (nodes: IMppNode[]): Set<string> => {
  const ids = new Set<string>();
  const visit = (n: IMppNode) => {
    if (n.children.length > 0) ids.add(n.id);
    n.children.forEach(visit);
  };
  nodes.forEach(visit);
  return ids;
};

const filterTree = (nodes: IMppNode[], q: string): [IMppNode[], Set<string>] => {
  const expanded = new Set<string>();
  const lower = q.toLowerCase();
  const filterNode = (n: IMppNode): IMppNode | null => {
    const filteredChildren = n.children
      .map(filterNode)
      .filter(Boolean) as IMppNode[];
    const selfMatch =
      n.label.toLowerCase().includes(lower) ||
      n.code.toLowerCase().includes(lower);
    if (selfMatch || filteredChildren.length > 0) {
      if (filteredChildren.length > 0) expanded.add(n.id);
      return { ...n, children: filteredChildren };
    }
    return null;
  };
  const result = nodes.map(filterNode).filter(Boolean) as IMppNode[];
  return [result, expanded];
};

const insertNode = (
  tree: IMppNode[],
  parentId: string,
  newNode: IMppNode,
): IMppNode[] =>
  tree.map((n) => {
    if (n.id === parentId) return { ...n, children: [...n.children, newNode] };
    return { ...n, children: insertNode(n.children, parentId, newNode) };
  });

const updateNode = (
  tree: IMppNode[],
  id: string,
  patch: { label: string; code: string },
): IMppNode[] =>
  tree.map((n) => {
    if (n.id === id) return { ...n, ...patch };
    return { ...n, children: updateNode(n.children, id, patch) };
  });

const removeNode = (tree: IMppNode[], id: string): IMppNode[] =>
  tree
    .filter((n) => n.id !== id)
    .map((n) => ({ ...n, children: removeNode(n.children, id) }));

// ── Węzeł drzewa ───────────────────────────────────────────────────────────────

interface TreeNodeProps {
  node: IMppNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onAdd: (parentId: string, parentLabel: string) => void;
  onEdit: (node: IMppNode) => void;
  onDelete: (id: string, label: string) => void;
  searchActive: boolean;
}

const TreeNode = ({
  node,
  depth,
  expanded,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
  searchActive,
}: TreeNodeProps) => {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);

  return (
    <div className={styles.nodeWrapper}>
      <div
        className={`${styles.nodeRow} ${depth === 0 ? styles.nodeRowL1 : ""} ${
          depth === 1 ? styles.nodeRowL2 : ""
        }`}
        style={{ paddingLeft: 8 + depth * 20 }}
      >
        <span
          className={styles.chevron}
          onClick={() => hasChildren && onToggle(node.id)}
          style={{ visibility: hasChildren ? "visible" : "hidden" }}
        >
          {isOpen || searchActive ? (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          )}
        </span>

        {node.code && <span className={styles.codeChip}>{node.code}</span>}

        <span className={styles.nodeLabel}>{node.label}</span>

        <span className={styles.nodeActions}>
          <Tooltip title="Dodaj podrzędny" placement="top" arrow>
            <span
              className={styles.actionBtn}
              onClick={(e) => {
                e.stopPropagation();
                onAdd(node.id, node.label);
              }}
            >
              <AddIcon sx={{ fontSize: 13 }} />
            </span>
          </Tooltip>
          <Tooltip title="Edytuj etykietę" placement="top" arrow>
            <span
              className={styles.actionBtn}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(node);
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 13 }} />
            </span>
          </Tooltip>
          <Tooltip title="Usuń" placement="top" arrow>
            <span
              className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(node.id, node.label);
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 13 }} />
            </span>
          </Tooltip>
        </span>
      </div>

      {hasChildren && (isOpen || searchActive) && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
              searchActive={searchActive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Komponent główny ──────────────────────────────────────────────────────────

const MppTab = () => {
  const [tree, setTree] = useState<IMppNode[]>(() => loadMppTree());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // parentId === null → dodanie korzenia
  const [addDialog, setAddDialog] = useState<{
    parentId: string | null;
    parentLabel: string;
  } | null>(null);
  const [editDialog, setEditDialog] = useState<{ id: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editLabel, setEditLabel] = useState("");

  const persist = useCallback((newTree: IMppNode[]) => {
    setTree(newTree);
    saveMppTree(newTree);
  }, []);

  const [filteredTree, searchExpanded] = useMemo(() => {
    if (!search.trim()) return [tree, new Set<string>()] as const;
    return filterTree(tree, search.trim());
  }, [tree, search]);

  const searchActive = search.trim().length > 0;

  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleExpandAll = () => setExpanded(collectAllIds(tree));
  const handleCollapseAll = () => setExpanded(new Set());

  // ── Dodawanie (korzeń lub podrzędny) ───────────────────────────────────────
  const openAddRoot = () => {
    setAddDialog({ parentId: null, parentLabel: "" });
    setNewCode("");
    setNewLabel("");
  };
  const openAdd = (parentId: string, parentLabel: string) => {
    setAddDialog({ parentId, parentLabel });
    setNewCode("");
    setNewLabel("");
  };
  const confirmAdd = () => {
    if (!addDialog || !newLabel.trim()) return;
    const node: IMppNode = {
      id: `mpp_${Date.now()}`,
      code: newCode.trim(),
      label: newLabel.trim(),
      children: [],
    };
    const newTree =
      addDialog.parentId === null
        ? [...tree, node]
        : insertNode(tree, addDialog.parentId, node);
    persist(newTree);
    if (addDialog.parentId)
      setExpanded((prev) => new Set([...prev, addDialog.parentId as string]));
    setAddDialog(null);
  };

  // ── Edycja (dowolny poziom) ────────────────────────────────────────────────
  const openEdit = (node: IMppNode) => {
    setEditDialog({ id: node.id });
    setEditCode(node.code);
    setEditLabel(node.label);
  };
  const confirmEdit = () => {
    if (!editDialog || !editLabel.trim()) return;
    persist(
      updateNode(tree, editDialog.id, {
        label: editLabel.trim(),
        code: editCode.trim(),
      }),
    );
    setEditDialog(null);
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    persist(removeNode(tree, deleteConfirm.id));
    setDeleteConfirm(null);
  };

  const displayTree = searchActive ? filteredTree : tree;
  const displayExpanded = searchActive ? searchExpanded : expanded;

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <TextField
          inputRef={searchRef}
          size="small"
          placeholder="Szukaj po symbolu lub etykiecie…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchBox}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  ✕
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.3)" }} />
              </InputAdornment>
            ),
          }}
        />
        <div className={styles.toolbarRight}>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddRoot}
          >
            Dodaj korzeń
          </Button>
          <Tooltip title="Rozwiń wszystko">
            <span>
              <IconButton size="small" onClick={handleExpandAll} disabled={searchActive}>
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zwiń wszystko">
            <span>
              <IconButton size="small" onClick={handleCollapseAll} disabled={searchActive}>
                <UnfoldLessIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className={styles.legend}>
        Buduj strukturę miejsc powstawania kosztów: korzenie, węzły i liście.
        <span className={styles.legendHint}>
          — ikony <AddIcon sx={{ fontSize: 11, verticalAlign: "middle" }} /> /{" "}
          <EditOutlinedIcon sx={{ fontSize: 11, verticalAlign: "middle" }} /> pojawiają się po najechaniu na wiersz
        </span>
      </div>

      <div className={styles.treeContainer}>
        {displayTree.length === 0 ? (
          <div className={styles.emptyMsg}>
            {searchActive
              ? `Brak wyników dla „${search}"`
              : "Brak struktury — dodaj pierwszy korzeń przyciskiem „Dodaj korzeń”."}
          </div>
        ) : (
          displayTree.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              expanded={displayExpanded}
              onToggle={handleToggle}
              onAdd={openAdd}
              onEdit={openEdit}
              onDelete={(id, label) => setDeleteConfirm({ id, label })}
              searchActive={searchActive}
            />
          ))
        )}
      </div>

      {/* Dialog: dodaj */}
      <Dialog open={!!addDialog} onClose={() => setAddDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          {addDialog?.parentId === null ? "Dodaj korzeń" : "Dodaj element podrzędny"}
        </DialogTitle>
        <DialogContent>
          {addDialog?.parentId !== null && (
            <div className={styles.dialogHint}>
              Element nadrzędny: <strong>{addDialog?.parentLabel}</strong>
            </div>
          )}
          <TextField
            label="Symbol (opcjonalny)"
            size="small"
            fullWidth
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            sx={{ mt: 1.5, mb: 2 }}
            placeholder="np. MPP-01"
          />
          <TextField
            label="Etykieta *"
            size="small"
            fullWidth
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmAdd()}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialog(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button onClick={confirmAdd} variant="contained" size="small" disabled={!newLabel.trim()}>
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: edytuj */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Edytuj element
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Symbol (opcjonalny)"
            size="small"
            fullWidth
            value={editCode}
            onChange={(e) => setEditCode(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Etykieta *"
            size="small"
            fullWidth
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmEdit()}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button onClick={confirmEdit} variant="contained" size="small" disabled={!editLabel.trim()}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: usuń */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Usuń element
        </DialogTitle>
        <DialogContent>
          Czy na pewno usunąć <strong>„{deleteConfirm?.label}"</strong>?
          <br />
          <span style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4, display: "block" }}>
            Zostaną usunięte również wszystkie elementy podrzędne.
          </span>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error" size="small">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MppTab;
